import { Link as RouterLink } from 'react-router-dom';
import { resolveBuildRunNumber } from '@/features/builds/utils/buildRunNumber';
import { buildJobDetailPath, buildSyntheticJobId } from '@/features/jobs/utils/jobPaths';
import { resolvePipelineSlug, toPipelineSlug } from '@/features/pipelines/utils/pipelineSlug';
import { buildStageDetailPath, buildSyntheticStageId } from '@/features/pipelines/utils/stagePaths';
import { mapBuildStageSummaryItems } from '@/features/pipelines/utils/stageSummaryLinks';
import { getBuildStatusIconComponent } from '@/features/shared/components/tableSystem/buildStatusIcons';
import { TableBuildStatusBreakdownCell } from '@/features/shared/components/tableSystem/TableBuildStatusBreakdownCell';
import {
  TableActionsCell,
  TableCellContainer,
} from '@/features/shared/components/tableSystem/TableCells';
import {
  resolveLatestChipBackground,
  resolveLatestChipForeground,
} from '@/features/shared/components/tableSystem/TableLatestCellStyles';
import {
  CLONE_META_DETAIL_GLYPH_SX,
  TableStatusMetaContent,
} from '@/features/shared/components/tableSystem/TableMetaCells';
import { TableStageStatusBreakdownCell } from '@/features/shared/components/tableSystem/TableStageStatusBreakdownCell';
import { resolveEntityTags, resolveTagColor } from '@/features/shared/tags';
import { resolveBuildDisplayStatus } from '@/features/shared/utils/ciStatus';
import { buildStatusOptions } from '@/shared/constants/statusOptions';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDetailLabel } from '@/shared/copy/details';
import { s } from '@/theme/tokens';
import { Box, formatDuration, formatStatusLabel, Text } from '@/ui';
import {
  getSingleSelectOptionLabel,
  getSingleSelectOptionValue,
} from '@/ui/composites/grid/gridColumns';
import {
  getSingleSelectFilterOperators,
  getStringFilterOperators,
} from '@/ui/composites/grid/gridOperators';
import { RunLinkCell, TableNameCell } from '@/ui/composites/table';
import { resolveIteratorLabel } from '@/ui/composites/table/cellContents/shared';
import { linkTruncateSx } from '@/ui/composites/table/cellContents/shared';
import {
  tableCellPrimaryTextSx,
  tableCellSecondaryTextSx,
  tableCellTagChipSx,
  tableCellTagRowSx,
  tableCellTextBlockSx,
} from '@/ui/composites/table/cells/sharedCellContent';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import { Inline } from '@/ui/layout/Inline';
import { Chip, Link, resolveStatusChipTone, StagesIcon, type GridColDef } from '@/ui/primitives';
import { EventOutlinedIcon, TimerOutlinedIcon } from '@/ui/primitives/icons';
import type { TagColor } from '@/shared/data/tags';
import type { SummaryTooltipItem } from '@/ui/composites/table/tableUtils';
import type { BuildDto, MetricThreshold } from '@flowqa/shared';
import type { ReactElement } from 'react';

const columnFlex = {
  run: 0.75,
  pipeline: 1,
  status: 0.75,
  jobs: 0.55,
  stages: 0.55,
  actions: 0.1,
};

const mapBuildJobSummaryItems = (row: BuildDto): SummaryTooltipItem[] =>
  (row.jobSummaries ?? []).map((build) => {
    const runNumber = resolveBuildRunNumber(build.buildKey ?? null);
    const pipelineSlug =
      row.pipelineId && row.pipelineName
        ? resolvePipelineSlug({
            id: row.pipelineId,
            name: row.pipelineName,
          })
        : null;

    return {
      key: build.id,
      detailLabel: row.pipelineName ?? 'Pipeline',
      executionLabel: runNumber === null ? null : `#${runNumber}`,
      href: pipelineSlug ? `/pipelines/${pipelineSlug}` : '/pipelines',
      executionHref:
        pipelineSlug && runNumber !== null ? `/pipelines/${pipelineSlug}/runs/${runNumber}` : null,
      runNumber,
      status: build.status,
      timestamp: build.timestamp,
    };
  });

type BuildColumnsOptions = {
  nameColumnMode?: 'pipeline' | 'job';
  nameColumnHeaderName?: string;
  showStageSummaryColumn?: boolean;
  showJobColumn?: boolean;
  showPipelineColumn?: boolean;
  durationThreshold: MetricThreshold;
  runHrefBuilder?: (row: BuildDto) => string | null;
  tagColorByName?: Partial<Record<string, TagColor>>;
  includeActionsColumn?: boolean;
  onEditRow?: (row: BuildDto) => void;
  onDeleteRow?: (row: BuildDto) => void;
  editRowPendingId?: string | null;
  deleteRowPendingId?: string | null;
};
export const buildBuildsColumns = ({
  nameColumnMode = 'pipeline',
  nameColumnHeaderName,
  showStageSummaryColumn = true,
  showJobColumn = false,
  showPipelineColumn = true,
  durationThreshold: _durationThreshold,
  runHrefBuilder,
  tagColorByName = {},
  includeActionsColumn = false,
  onEditRow,
  onDeleteRow,
  editRowPendingId = null,
  deleteRowPendingId = null,
}: BuildColumnsOptions): Array<GridColDef<BuildDto>> => {
  const stringOperators = getStringFilterOperators();
  const statusOperators = getSingleSelectFilterOperators();

  const runColumn: GridColDef<BuildDto> = {
    field: 'buildKey',
    headerName: getDetailLabel('run'),
    flex: columnFlex.run,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-run-link-cell',
    filterable: false,
    sortable: false,
    valueGetter: (_value, row) => row.buildKey ?? null,
    renderCell: (params): ReactElement => {
      const runNumber = resolveBuildRunNumber(params.row.buildKey);
      const label = runNumber !== null ? resolveIteratorLabel(runNumber) : 'View';
      const isLatest = params.row.isLatest === true;
      const pipelineSlug =
        params.row.pipelineName && params.row.pipelineName.length > 0
          ? toPipelineSlug(params.row.pipelineName)
          : null;
      const pipelineSegment =
        pipelineSlug && pipelineSlug.length > 0 ? pipelineSlug : (params.row.pipelineId ?? null);
      const href =
        runHrefBuilder?.(params.row) ??
        (pipelineSegment && runNumber !== null
          ? `/pipelines/${pipelineSegment}/runs/${runNumber}`
          : '/pipelines');
      return (
        <TableCellContainer gap="none">
          <RunLinkCell label={label} href={href} latest={isLatest} />
        </TableCellContainer>
      );
    },
  };

  const pipelineColumn: GridColDef<BuildDto> = {
    field: 'pipelineName',
    headerName: nameColumnHeaderName ?? 'Pipeline',
    flex: columnFlex.pipeline,
    cellClassName: 'table-name-cell',
    valueGetter: (_value, row) => row.pipelineName ?? null,
    renderCell: (params): ReactElement => {
      const label = params.row.pipelineName;
      if (!label) {
        return (
          <Text role="body" component="span">
            -
          </Text>
        );
      }
      const pipelineSlug = toPipelineSlug(label);
      const pipelineSegment =
        pipelineSlug.length > 0 ? pipelineSlug : (params.row.pipelineId ?? null);
      return (
        <TableCellContainer gap="none">
          <TableNameCell
            primary={{
              label,
              href: pipelineSegment ? `/pipelines/${pipelineSegment}` : '/pipelines',
            }}
            tags={resolveEntityTags(params.row).map((tag) => ({
              label: tag,
              tone: resolveTagColor(tag, tagColorByName),
            }))}
          />
        </TableCellContainer>
      );
    },
  };
  pipelineColumn.filterOperators = stringOperators;

  const jobColumn: GridColDef<BuildDto> = {
    field: 'jobName',
    headerName: nameColumnHeaderName ?? 'Job',
    flex: columnFlex.pipeline,
    cellClassName: 'table-name-cell',
    valueGetter: (_value, row) => row.jobName ?? null,
    renderCell: (params): ReactElement => {
      const jobName = params.row.jobName;
      if (!jobName) {
        return (
          <Text role="body" component="span">
            -
          </Text>
        );
      }
      const jobHref = buildJobDetailPath(
        buildSyntheticJobId({
          pipelineId: params.row.pipelineId ?? null,
          stageName: params.row.stageName ?? null,
          jobName,
        }),
      );
      const pipelineHref =
        params.row.pipelineId && params.row.pipelineName
          ? `/pipelines/${resolvePipelineSlug({
              id: params.row.pipelineId,
              name: params.row.pipelineName,
            })}`
          : null;
      const stageHref = params.row.stageName
        ? buildStageDetailPath(
            buildSyntheticStageId(params.row.pipelineId ?? null, params.row.stageName),
          )
        : null;

      return (
        <TableCellContainer gap="none">
          <Box sx={tableCellTextBlockSx}>
            <Text role="body" dataGridTypography="custom" sx={tableCellPrimaryTextSx}>
              <Link
                component={RouterLink}
                to={jobHref}
                data-grid-typography="custom"
                underline="hover"
                color="inherit"
                onClick={(event) => {
                  event.stopPropagation();
                }}
                sx={linkTruncateSx}
              >
                {jobName}
              </Link>
            </Text>
            {(showPipelineColumn && params.row.pipelineName) || params.row.stageName ? (
              <Text
                role="body"
                dataGridTypography="custom"
                color="textSecondary"
                sx={tableCellSecondaryTextSx}
              >
                {showPipelineColumn && params.row.pipelineName ? (
                  pipelineHref ? (
                    <Link
                      component={RouterLink}
                      to={pipelineHref}
                      data-grid-typography="custom"
                      underline="hover"
                      color="inherit"
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                    >
                      {params.row.pipelineName}
                    </Link>
                  ) : (
                    params.row.pipelineName
                  )
                ) : null}
                {showPipelineColumn && params.row.pipelineName && params.row.stageName
                  ? ' / '
                  : null}
                {params.row.stageName ? (
                  stageHref ? (
                    <Link
                      component={RouterLink}
                      to={stageHref}
                      data-grid-typography="custom"
                      underline="hover"
                      color="inherit"
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                    >
                      {params.row.stageName}
                    </Link>
                  ) : (
                    params.row.stageName
                  )
                ) : null}
              </Text>
            ) : null}
            {resolveEntityTags(params.row).length > 0 ? (
              <Box sx={[tableCellTagRowSx, { mt: s('sm') }]}>
                <Inline gap="sm" wrap="wrap" align="center" sx={{ width: '100%', minWidth: 0 }}>
                  {resolveEntityTags(params.row).map((tag) => (
                    <Chip
                      key={tag}
                      size="xs"
                      tone={resolveTagColor(tag, tagColorByName)}
                      label={tag}
                      sx={tableCellTagChipSx}
                    />
                  ))}
                </Inline>
              </Box>
            ) : null}
          </Box>
        </TableCellContainer>
      );
    },
  };
  jobColumn.filterOperators = stringOperators;

  const statusColumn: GridColDef<BuildDto> = {
    field: 'status',
    headerName: getDetailLabel('status'),
    flex: columnFlex.status,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-status-meta-cell',
    type: 'singleSelect',
    valueOptions: buildStatusOptions,
    getOptionLabel: getSingleSelectOptionLabel,
    getOptionValue: getSingleSelectOptionValue,
    filterOperators: statusOperators,
    renderCell: (params): ReactElement => {
      const status = resolveBuildDisplayStatus(params.row) ?? params.row.status;
      const label = formatStatusLabel(status);
      const statusTone = resolveStatusChipTone(status);
      const durationMs =
        params.row.startedAt && params.row.finishedAt
          ? Math.max(params.row.finishedAt.getTime() - params.row.startedAt.getTime(), 0)
          : null;
      const durationLabel = formatDuration(durationMs);
      const timestamp = params.row.finishedAt ?? params.row.startedAt ?? null;
      const timestampLabel = timestamp ? formatRelativeTime(timestamp) : '';
      const timestampTitle = timestamp ? formatDate(timestamp, { includeTime: true }) : '';
      const runNumber = resolveBuildRunNumber(params.row.buildKey);
      const pipelineSlug =
        params.row.pipelineName && params.row.pipelineName.length > 0
          ? toPipelineSlug(params.row.pipelineName)
          : null;
      const pipelineSegment =
        pipelineSlug && pipelineSlug.length > 0 ? pipelineSlug : (params.row.pipelineId ?? null);
      const statusHref =
        runHrefBuilder?.(params.row) ??
        (pipelineSegment && runNumber !== null
          ? `/pipelines/${pipelineSegment}/runs/${runNumber}`
          : null);
      const StatusIcon = getBuildStatusIconComponent(status);

      return (
        <TableCellContainer gap="none">
          <TableStatusMetaContent
            statusLabel={label.length > 0 ? label : '-'}
            statusHref={statusHref}
            chipTone={statusTone}
            chipIcon={<Box component={StatusIcon} />}
            chipBackgroundColor={(theme) => resolveLatestChipBackground(theme, statusTone)}
            chipForegroundColor={(theme) => resolveLatestChipForeground(theme, statusTone)}
            durationLabel={durationLabel}
            durationIcon={<TimerOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
            timestampLabel={timestampLabel}
            timestampTitle={timestampTitle}
            timestampIcon={<EventOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
          />
        </TableCellContainer>
      );
    },
  };

  const stagesColumn: GridColDef<BuildDto> = {
    field: 'stageStatusCounts',
    headerName: 'Stages',
    flex: columnFlex.stages,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-breakdown-cell',
    filterable: false,
    sortable: false,
    renderCell: (params): ReactElement => {
      const total = params.row.stageTotal ?? 0;

      return (
        <TableCellContainer gap="none">
          <TableStageStatusBreakdownCell
            total={total}
            counts={params.row.stageStatusCounts}
            summaryItems={mapBuildStageSummaryItems(params.row)}
          />
        </TableCellContainer>
      );
    },
  };

  const jobsColumn: GridColDef<BuildDto> = {
    field: 'jobStatusCounts',
    headerName: 'Jobs',
    flex: columnFlex.jobs,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-breakdown-cell',
    filterable: false,
    sortable: false,
    renderCell: (params): ReactElement => {
      const total = params.row.jobTotal ?? 0;

      return (
        <TableCellContainer gap="none">
          <TableBuildStatusBreakdownCell
            total={total}
            counts={params.row.jobStatusCounts}
            summaryItems={mapBuildJobSummaryItems(params.row)}
            totalIcon={StagesIcon}
            showDateHeaders={false}
          />
        </TableCellContainer>
      );
    },
  };

  const actionsColumn: GridColDef<BuildDto> = {
    field: 'actions',
    headerName: '',
    flex: columnFlex.actions,
    minWidth: 56,
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params): ReactElement => (
      <TableActionsCell
        rowName={params.row.buildKey ?? params.row.id}
        editActionLabel={getEntityActionLabel('edit', 'Job')}
        deleteActionLabel={getEntityActionLabel('delete', 'Job')}
        onEdit={() => {
          onEditRow?.(params.row);
        }}
        onDelete={() => {
          onDeleteRow?.(params.row);
        }}
        disableEdit={editRowPendingId === params.row.id || deleteRowPendingId === params.row.id}
        disableDelete={deleteRowPendingId === params.row.id}
      />
    ),
  };

  const leadingNameColumn = nameColumnMode === 'job' ? jobColumn : pipelineColumn;
  const leadingColumns = [
    leadingNameColumn,
    runColumn,
    statusColumn,
    ...(showStageSummaryColumn ? [stagesColumn] : []),
    ...(showJobColumn && nameColumnMode !== 'job' ? [jobsColumn] : []),
  ];

  return includeActionsColumn ? [...leadingColumns, actionsColumn] : leadingColumns;
};
